import { useMemo, useEffect, useState } from 'react';
import {
  Heading,
  VStack,
  HStack,
  Spacer,
  Button,
  Text,
  Icon,
  Flex,
  Box,
  useDisclosure,
  SimpleGrid,
  Divider,
  Stack,
} from '@chakra-ui/react';
import PageContainer from '~/containers/PageContainer';
import Content from '~/containers/Content';
import FooterCartStatusBar from '~/components/FooterCartStatusBar';
import FooterLoginSection from '~/components/FooterLoginSection';
import FooterReturnNotice from '~/components/FooterReturnNotice';
import LocationBar from '~/components/LocationBar';
import GoTopButton from '~/components/GoTopButton';
import { IoCashOutline, IoChevronDown } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import platformAtom from '~/recoil/atom/platform';
import authAtom from '~/recoil/atom/auth';
import { useRecoilValue, useRecoilState } from 'recoil';
import useCartId from '~/hooks/useCartId';
import * as fp from 'lodash/fp';
import { CartType } from '~/types/cart';
import { FixedHeaderSection, SearchInput, TagFilterModal } from '../Home/components';
import useEnterpriseGroupBuyStoreList from '~/hooks/useEnterpriseGroupBuyStoreList';
import EmptySection from '~/components/EmptySection';
import MealItemCard from '~/components/MealItemCard';
import { PLATFORM_ID } from '~/globalVariables';
import SectionContainer from '~/containers/SectionContainer';
import { ImSpoonKnife } from 'react-icons/im';
import enterpriseGroupBuyFilterMealSearchFormAtom from '~/recoil/atom/enterpriseGroupBuy/enterpriseGroupBuyFilterMealSearchForm';
import globalEnterpriseGroupBuyAtom from '~/recoil/atom/enterpriseGroupBuy/globalEnterpriseGroupBuyAtom';
import { WiTime3 } from 'react-icons/wi';
import { zhTW } from 'date-fns/locale';
import { format } from 'date-fns';
import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import { EnterpriseGroupBuyStatus, PaymentTypeString } from '~/types/enterpriseGroupBuy';
import { BsPersonFill } from 'react-icons/bs';
import { AiOutlineMessage } from 'react-icons/ai';
import { FilterInfoBar } from '../FilterMeal/components';
import StoreLastPreOrderTimeNotice from '~/components/StoreLastPreOrderTimeNotice';

export default function Home() {
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);
  const auth = useRecoilValue(authAtom);
  const platform = useRecoilValue(platformAtom);
  const { enterpriseGroupBuyId = '' } = useParams();
  const { data } = useRecoilValue(globalEnterpriseGroupBuyAtom);
  const [filterMealSearchForm, setFilterMealSearchFrom] = useRecoilState(
    enterpriseGroupBuyFilterMealSearchFormAtom,
  );

  const { cartType } = useCartId();

  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState<string>(filterMealSearchForm.searchText);

  const {
    isOpen: isTagFilterOpen,
    onClose: onTagFilterClose,
    onOpen: onTagFilterOpen,
  } = useDisclosure();

  useEffect(() => {
    setSearchInput(filterMealSearchForm.searchText);
  }, [filterMealSearchForm.searchText]);

  const { storeList } = useEnterpriseGroupBuyStoreList(
    data?.pickMealAt ? data?.pickMealAt?.toDate() : null,
  );

  const filteredStores = useMemo(() => {
    if (filterMealSearchForm.searchText || filterMealSearchForm.tags.length > 0) {
      return storeList
        .map((store) => ({
          ...store,
          items: store?.storeData?.name.includes(filterMealSearchForm.searchText)
            ? store.items
            : store.items.filter((i) => i.name.includes(filterMealSearchForm.searchText)),
        }))
        .map((store) => ({
          ...store,
          items: store.items.filter((i) =>
            !!i?.tags?.[PLATFORM_ID] && filterMealSearchForm.tags.length > 0
              ? (filterMealSearchForm.tags || []).some((tag) =>
                  (i?.tags?.[PLATFORM_ID] || []).join('')?.includes(tag),
                )
              : true,
          ),
        }))
        .filter((store) => store.items?.length > 0);
    }

    return storeList;
  }, [filterMealSearchForm.searchText, filterMealSearchForm.tags, JSON.stringify(storeList || [])]);

  const handleOnNavigateToPath = (path: string) => {
    return navigate(`/enterpriseGroupBuy/${enterpriseGroupBuyId}/${path}`);
  };

  return (
    <PageContainer topBoxStyle={{ position: 'sticky' }}>
      <FixedHeaderSection>
        <LocationBar />
        <FilterInfoBar
          pickTimeString={
            data?.pickMealAt && data?.pickMealEnd
              ? format(data?.pickMealAt.toDate(), 'M/d(eee) HH:mm', { locale: zhTW }) +
                format(data?.pickMealEnd.toDate(), '-HH:mm', { locale: zhTW })
              : '-'
          }
        />
      </FixedHeaderSection>

      <Box>
        <Box bgColor="gray.100">
          <LocationBar />
          <VStack
            m="20px auto"
            w="fit-content"
            bg="#FFF"
            borderRadius="5px"
            p="8px 16px"
            border="1px solid #E5B369"
            spacing="16px"
          >
            <Text fontSize="16px" fontWeight={700} color="#333">
              {data?.name || '-'}的團購
            </Text>
            <Text fontSize="16px" w="full" fontWeight={700} color="#333" textAlign="left">
              {data?.status === EnterpriseGroupBuyStatus.ONGOING
                ? '正在團購中'
                : data?.status === EnterpriseGroupBuyStatus.RECEIVED
                ? '餐點準備中'
                : ''}
            </Text>
            <VStack spacing="8px" divider={<Divider borderColor="#E5B369" />}>
              <HStack
                w="full"
                minW={isLargerThan768 ? '480px' : '343px'}
                align="center"
                justify="space-between"
                fontSize="14px"
                fontWeight={400}
                color="#333"
              >
                <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                  {<Icon as={WiTime3} fontSize="20px" />}
                  <Text>取餐時間</Text>
                </HStack>
                <Text fontWeight={700}>
                  {data?.pickMealAt && data?.pickMealEnd
                    ? format(data?.pickMealAt.toDate(), 'M/d(eee) HH:mm', { locale: zhTW }) +
                      format(data?.pickMealEnd.toDate(), '-HH:mm', { locale: zhTW })
                    : '-'}
                </Text>
              </HStack>
              <HStack
                w="full"
                minW={isLargerThan768 ? '480px' : '343px'}
                align="center"
                justify="space-between"
                fontSize="14px"
                fontWeight={400}
                color="#333"
              >
                <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                  {<Icon as={IoCashOutline} fontSize="20px" />}
                  <Text>付款方式</Text>
                </HStack>
                <Text fontWeight={700}>
                  {data?.paymentType ? PaymentTypeString?.[data?.paymentType] : '-'}
                </Text>
              </HStack>
            </VStack>
            <VStack>
              <HStack
                w="full"
                minW={isLargerThan768 ? '480px' : '343px'}
                align="center"
                justify="space-between"
                fontSize="14px"
                fontWeight={400}
                color="#333"
              >
                <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                  {<Icon as={BsPersonFill} fontSize="20px" />}
                  <Text>主辦人</Text>
                </HStack>
                <Text fontWeight={700}>{data?.sponsorName || '-'}</Text>
              </HStack>
              <HStack
                w="full"
                minW={isLargerThan768 ? '480px' : '343px'}
                align="center"
                justify="space-between"
                fontSize="14px"
                fontWeight={400}
                color="#333"
              >
                <HStack flex={1} spacing="8px" align="center" justify="flex-start">
                  <Text />
                </HStack>
                <Text fontWeight={700}>{data?.sponsorPhone || '-'}</Text>
              </HStack>

              {data?.sponsorTalk ? (
                <>
                  <Text
                    p="8px"
                    borderRadius="5px"
                    w="100%"
                    bg="#F9F9F9"
                    fontSize="14px"
                    fontWeight={400}
                  >
                    <Icon as={AiOutlineMessage} fontSize="18px" /> {data?.sponsorTalk || ''}
                  </Text>
                </>
              ) : null}
            </VStack>
          </VStack>
          <Content>
            <VStack m="auto" maxW="800px" align="stretch" spacing={4} px={3} py={6}>
              <Heading size="md" color="secondary" fontWeight="normal" textAlign="center">
                搜尋餐點
              </Heading>
              <Stack direction={isLargerThan768 ? 'row' : 'column'} spacing="16px">
                <Flex flex={1} h="48px" maxH="48px">
                  <form
                    style={{ width: '100%' }}
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setFilterMealSearchFrom((prev) => ({
                        ...prev,
                        searchText: searchInput || '',
                      }));
                    }}
                  >
                    <SearchInput
                      flex={1}
                      w="full"
                      value={searchInput}
                      type="text"
                      onChange={(e) => setSearchInput(e.target.value)}
                      onSearch={async () => {
                        setFilterMealSearchFrom((prev) => ({
                          ...prev,
                          searchText: searchInput || '',
                        }));
                      }}
                      onClear={() => {
                        setSearchInput('');
                        setFilterMealSearchFrom((prev) => ({
                          ...prev,
                          searchText: '',
                        }));
                      }}
                    />
                  </form>
                </Flex>
                <Button
                  h="48px"
                  minH="48px"
                  onClick={onTagFilterOpen}
                  flex={1}
                  justifyContent="space-between"
                  bgColor="white"
                  boxShadow="0px 0px 10px rgba(0, 0, 0, 0.1)"
                  rounded={10}
                  _hover={{}}
                  _focus={{}}
                  size="lg"
                >
                  <Text isTruncated>{filterMealSearchForm.tags.join(',') || '全部素食分類'}</Text>
                  <Icon as={IoChevronDown} color="primary" boxSize="1.5rem" />
                </Button>
              </Stack>
            </VStack>
          </Content>
        </Box>
        <Box bg="white">
          <Content minH="70vh" onScroll={(e) => e.stopPropagation()}>
            <Content minH="60vh">
              <VStack align="stretch" p={4} spacing={8}>
                {(filteredStores || []).reduce(
                  (prev, curr) => prev + (curr.items?.length || 0),
                  0,
                ) <= 0 ? (
                  <EmptySection content="目前無可選擇餐點" />
                ) : (
                  <>
                    <SectionContainer
                      title="全部餐點"
                      icon={<Icon as={ImSpoonKnife} color="secondary" boxSize="1.25rem" />}
                    />
                    {!!filterMealSearchForm?.searchText ||
                    filterMealSearchForm?.tags?.length > 0 ? (
                      <Text fontSize="16px" fontWeight={400} color="#787878">
                        符合搜尋結果:{' '}
                        {filteredStores.reduce(
                          (currentCount, store) => currentCount + (store?.items?.length || 0),
                          0,
                        )}{' '}
                        個
                      </Text>
                    ) : null}
                    {filteredStores.map(({ items, storeData }, index) => {
                      return (
                        <VStack align="stretch" key={`${storeData?.id || ''}${index}`}>
                          <Heading fontSize="lg">{storeData?.name}</Heading>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                            {items.map((item) => (
                              <MealItemCard
                                key={`${storeData.id}_${item.id}`}
                                name={item.name}
                                description={item.description}
                                tags={item.tags[PLATFORM_ID] || []}
                                price={item.price}
                                image={item.image}
                                onNavigate={() => {
                                  handleOnNavigateToPath(
                                    `store/${storeData.id}/item/${item.id}/add`,
                                  );
                                }}
                              />
                            ))}
                          </SimpleGrid>
                        </VStack>
                      );
                    })}
                  </>
                )}
              </VStack>
            </Content>
          </Content>

          <Box position="sticky" bottom="0">
            <Box>
              <Content>
                <Flex px={4} pb={4}>
                  <Spacer />
                  <GoTopButton />
                </Flex>
                {data?.orderEndAt ? (
                  <StoreLastPreOrderTimeNotice
                    isEnterpriseGroupBuy
                    isValidTime
                    time={data?.orderEndAt.toDate()}
                  />
                ) : null}
              </Content>
            </Box>

            <FooterReturnNotice />
            <>
              {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
                <FooterLoginSection />
              ) : (
                <FooterCartStatusBar onNavigate={() => handleOnNavigateToPath('cart')} />
              )}
            </>
          </Box>

          {/* <StoreFilterModal
            isOpen={isStoreFilterOpen}
            onClose={onStoreFilterClose}
            onConfirm={(stores: Array<string>) => {
              setFilterMealSearchFrom((prev) => ({ ...prev, storeIds: stores }));
              onStoreFilterClose();
            }}
            selectedStoreIds={filterMealSearchForm.storeIds}
            stores={storeList}
          /> */}
          <TagFilterModal
            isOpen={isTagFilterOpen}
            onClose={onTagFilterClose}
            onConfirm={(tags: Array<string>) => {
              setFilterMealSearchFrom((prev) => ({ ...prev, tags: tags }));
              onTagFilterClose();
            }}
            selectedTags={filterMealSearchForm.tags}
            tags={platform.tags}
          />
        </Box>
      </Box>
    </PageContainer>
  );
}
